/** App shell: sidebar, transcript, composer. */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { X } from "lucide-react";
import { api } from "./api.ts";
import { useChat, type LoadTarget, type SendSettings } from "./state/useChat.ts";
import {
  SIDEBAR_RAIL_WIDTH,
  useSidebarWidth,
} from "./state/useSidebarWidth.ts";
import { Sidebar } from "./components/Sidebar.tsx";
import { SidebarSheet } from "./components/SidebarSheet.tsx";
import { MobileBar } from "./components/MobileBar.tsx";
import { SidebarResizer } from "./components/SidebarResizer.tsx";
import { Composer } from "./components/composer/Composer.tsx";
import { EmptyState } from "./components/EmptyState.tsx";
import { AssistantMessage, UserMessage } from "./components/MessageView.tsx";
import { Alert, AlertDescription } from "./components/ui/alert.tsx";
import { Button } from "./components/ui/button.tsx";
import { ScrollArea } from "./components/ui/scroll-area.tsx";
import { DEFAULT_MODEL } from "../shared/models.ts";
import { DEFAULT_DEPTH_CHOICE } from "../shared/depth.ts";
import type { Conversation } from "../shared/types.ts";
import type { Highlight } from "../shared/highlights.ts";

/** Also the baseline the composer's chips measure "switched on" against. */
const DEFAULT_SETTINGS: SendSettings = {
  model: DEFAULT_MODEL,
  thinking: false,
  reasoningEffort: "medium",
  depth: DEFAULT_DEPTH_CHOICE,
  supervised: true,
};

export function App() {
  const [conversations, setConversations] = useState<readonly Conversation[]>([]);
  const [target, setTarget] = useState<LoadTarget>({ id: null, nonce: 0 });
  const [sidebarOpen, setSidebarOpen] = useState(true);
  /**
   * The drawer's state below `md`. Deliberately separate from `sidebarOpen`:
   * that one means "expanded rather than collapsed to the icon rail", which is
   * a persistent desktop preference, and it must not be flipped by opening a
   * drawer on a phone.
   */
  const [navOpen, setNavOpen] = useState(false);
  const [settings, setSettings] = useState<SendSettings>(DEFAULT_SETTINGS);
  const [highlights, setHighlights] = useState<readonly Highlight[]>([]);

  const { width: sidebarWidth, setWidth, reset } = useSidebarWidth();
  const [resizing, setResizing] = useState(false);

  const refresh = useCallback(async () => {
    try {
      setConversations(await api.listConversations());
    } catch {
      // A stale sidebar is survivable; the transcript still works.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);


  /**
   * The server creates the conversation on the first message. We only refresh
   * the list — deliberately *not* re-targeting, which would reload the thread
   * and throw away the answer currently streaming into it.
   */
  const onConversationCreated = useCallback(
    (_id: string) => {
      void refresh();
    },
    [refresh],
  );

  const onTitleChanged = useCallback(
    (id: string, title: string) => {
      setConversations((current) => {
        const known = current.some((conversation) => conversation.id === id);
        if (!known) {
          void refresh();
          return current;
        }
        return current.map((conversation) =>
          conversation.id === id ? { ...conversation, title } : conversation,
        );
      });
    },
    [refresh],
  );

  const { state, send, stop, dismissError } = useChat(
    target,
    onConversationCreated,
    onTitleChanged,
  );

  const activeConversationId = state.conversationId;

  useEffect(() => {
    if (!activeConversationId) {
      setHighlights([]);
      return;
    }

    let cancelled = false;
    void api
      .highlights(activeConversationId)
      .then((loaded) => {
        if (!cancelled) setHighlights(loaded);
      })
      .catch(() => {
        // Annotations are an enhancement; the answer reads fine without them.
      });

    return () => {
      cancelled = true;
    };
  }, [activeConversationId]);

  const addHighlight = useCallback(
    async (messageId: string, quote: string) => {
      try {
        const created = await api.createHighlight({ messageId, quote });
        setHighlights((current) => [...current, created]);
      } catch {
        // Nothing to recover: the passage is still on screen, just unmarked.
      }
    },
    [],
  );

  const selectConversation = useCallback((id: string) => {
    setTarget((current) => ({ id, nonce: current.nonce + 1 }));
  }, []);

  const startNew = useCallback(() => {
    setTarget((current) => ({ id: null, nonce: current.nonce + 1 }));
  }, []);

  const removeConversation = useCallback(async (id: string) => {
    try {
      await api.deleteConversation(id);
    } catch {
      return;
    }
    setConversations((current) => current.filter((c) => c.id !== id));
    setTarget((current) =>
      current.id === id ? { id: null, nonce: current.nonce + 1 } : current,
    );
  }, []);

  const busy = state.phase !== "idle";
  const isEmpty = state.messages.length === 0 && !state.streaming;

  // Highlight the thread actually being written to, which may be one the
  // server created a moment ago rather than the one we asked to load.
  const activeId = state.conversationId ?? target.id;

  const scrollDeps = useMemo(
    () => [state.messages.length, state.streaming?.content.length ?? 0] as const,
    [state.messages.length, state.streaming?.content.length],
  );
  const transcriptRef = useAutoScroll(scrollDeps);

  /**
   * One custom property drives both the grid column and the grip that sits on
   * the seam between the two tracks. Collapsing is the same mechanism as
   * resizing — it just pins the column to the rail width.
   */
  const sidebarStyle = useMemo(
    () =>
      ({
        "--sidebar-width": `${sidebarOpen ? sidebarWidth : SIDEBAR_RAIL_WIDTH}px`,
      }) as CSSProperties,
    [sidebarOpen, sidebarWidth],
  );

  return (
    /**
     * Exactly one viewport tall, never scrolling as a page. `min-h-0` on the
     * grid children is what enforces that — without it a track sizes to its
     * content, the body grows, and the sidebar and composer slide away with
     * the transcript instead of staying put.
     */
    <div
      data-sidebar={sidebarOpen ? "expanded" : "collapsed"}
      style={sidebarStyle}
      data-resizing={resizing ? "" : undefined}
      /* The eased transition is right for the collapse toggle and wrong for a
         drag, where it turns every pointer move into a 150ms lag — so a drag
         in progress opts out of it. */
      /* One column below `md`, where the sidebar is an overlay and owns no
         track at all; two from `md` up. */
      className="relative grid h-dvh grid-cols-1 overflow-hidden transition-[grid-template-columns] duration-150 ease-out md:grid-cols-[var(--sidebar-width)_1fr] data-resizing:transition-none"
    >
      <Sidebar
        conversations={conversations}
        activeId={activeId}
        onSelect={selectConversation}
        onCreate={startNew}
        onDelete={(id) => void removeConversation(id)}
        open={sidebarOpen}
        onToggle={() => setSidebarOpen((value) => !value)}
      />

      <SidebarSheet
        conversations={conversations}
        activeId={activeId}
        onSelect={selectConversation}
        onCreate={startNew}
        onDelete={(id) => void removeConversation(id)}
        open={navOpen}
        onOpenChange={setNavOpen}
      />

      {/* Collapsed, the rail has one fixed width and nothing to drag to — and
          below `md` there is no seam to put a grip on. */}
      {sidebarOpen && (
        <SidebarResizer
          width={sidebarWidth}
          onResize={setWidth}
          onReset={reset}
          onDraggingChange={setResizing}
        />
      )}

      {/*
        A column, not a three-row grid. The top bar is `display: none` from `md`
        up, and a hidden grid item creates no row — so the transcript would slide
        into the `auto` track meant for the bar, the composer into the `1fr`
        track meant for the transcript, and the composer would float in the
        middle of the page with dead space beneath it. Flex just drops the
        hidden child.
      */}
      <main className="flex h-dvh min-h-0 min-w-0 flex-col bg-background">
        <MobileBar onOpenNav={() => setNavOpen(true)} />
        {/*
          The one scrolling region in the app.

          It is named and focusable so that a keyboard-only reader can put the
          caret in it and page through a long derivation. Without `tabindex` the
          only way to move it is to tab through every link and figure control
          inside, which on a supervised answer is a lot of stops.
        */}
        <ScrollArea
          viewportRef={transcriptRef}
          viewportProps={{
            tabIndex: 0,
            role: "region",
            "aria-label": "Transcript",
          }}
          viewportClassName="scroll-smooth [overscroll-behavior:contain] motion-reduce:scroll-auto"
          className="min-h-0 flex-1"
        >
          <div className="mx-auto w-full max-w-measure px-gutter pt-8 pb-4">
            {isEmpty ? (
              <EmptyState onPick={(prompt) => send(prompt, settings)} />
            ) : (
              <>
                {state.messages.map((message) =>
                  message.role === "user" ? (
                    <UserMessage key={message.id} message={message} />
                  ) : (
                    <AssistantMessage
                      key={message.id}
                      content={message.content}
                      plan={message.plan}
                      review={message.review}
                      followUps={message.followUps}
                      reasoning={message.reasoning}
                      onAsk={(prompt) => send(prompt, settings)}
                      busy={busy}
                      highlights={highlights.filter(
                        (h) => h.messageId === message.id,
                      )}
                      onHighlight={(quote) =>
                        void addHighlight(message.id, quote)
                      }
                    />
                  ),
                )}

                {state.streaming && (
                  <AssistantMessage
                    content={state.streaming.content}
                    agents={state.streaming.agents}
                    agentDetail={state.streaming.agentDetail}
                    plan={state.streaming.plan}
                    review={state.streaming.review}
                    followUps={state.streaming.followUps}
                    reasoning={
                      state.streaming.reasoning.length > 0
                        ? state.streaming.reasoning
                        : null
                    }
                    streaming
                  />
                )}
              </>
            )}

            {state.error && (
              <Alert variant="destructive" className="mb-6 font-sans">
                <AlertDescription className="flex items-center gap-3">
                  <span className="flex-1">{state.error}</span>
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={dismissError}
                    aria-label="Dismiss"
                  >
                    <X />
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </div>
        </ScrollArea>

        {/* Pinned to the bottom of the stage — outside the scrolling row. The
            bottom padding eases off on short screens, where a phone's software
            keyboard has already taken most of the height. */}
        <div className="shrink-0 border-t bg-background px-gutter pt-3 pb-3 sm:pb-6">
          <div className="mx-auto w-full max-w-measure">
            <Composer
              onSend={send}
              onStop={stop}
              busy={busy}
              settings={settings}
              onSettingsChange={setSettings}
              defaultSettings={DEFAULT_SETTINGS}
              onRefine={api.refine}
            />
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Follow the answer as it streams, but let go the moment the reader scrolls up.
 * Yanking someone back to the bottom while they re-read a derivation is the
 * fastest way to make a study tool unusable.
 *
 * The scroll itself is deferred to the next animation frame, and only one frame
 * is ever queued. Tokens arrive far faster than the screen refreshes, and the
 * naive version read `scrollHeight` and wrote `scrollTop` on every single one —
 * a forced synchronous layout per token, on a document that is simultaneously
 * typesetting KaTeX. Coalescing to one write per frame costs nothing visually
 * and takes that cost off the streaming path.
 */
function useAutoScroll(deps: readonly unknown[]) {
  const ref = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);
  const frame = useRef<number | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const onScroll = () => {
      const distance =
        element.scrollHeight - element.scrollTop - element.clientHeight;
      pinned.current = distance < 120;
    };

    element.addEventListener("scroll", onScroll, { passive: true });
    return () => element.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!pinned.current || frame.current !== null) return;

    frame.current = requestAnimationFrame(() => {
      frame.current = null;
      const element = ref.current;
      // Re-checked inside the frame: the reader may have scrolled away in the
      // time between the token arriving and this running.
      if (element && pinned.current) {
        element.scrollTop = element.scrollHeight;
      }
    });
  }, deps);

  // A frame still queued when the transcript unmounts would touch a dead node.
  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );

  return ref;
}
